/**
 * Feature E03-F05 — "Редактирование, регенерация и approve документов".
 *
 * Request/response contracts for the stage-4 document editor: save an edited
 * artifact, approve it, regenerate a single document with a custom instruction,
 * and confirm the whole package.
 *
 * 1:1 port of the legacy n8n branches (docs/source-n8n/reference/{Regen,Approve,
 * Confirm}.md; sql-queries.md). The legacy SPA shipped these as the universal
 * `update_project` action variants (`save_stage4_artifact`, `approve_artifact`)
 * plus the `regenerate_document` and `confirm_package` router commands. v2 turns
 * each into a clean REST endpoint, but the data shapes are preserved exactly.
 *
 * IDOR invariant (carried over unchanged): the actor identity (`created_by`) is
 * ALWAYS the session user passed by the api side, NEVER a body field — so it is
 * intentionally absent from every *input* schema here.
 *
 * Lives in its OWN file. The serial Wire pass adds the `export *` line to
 * `packages/shared/src/index.ts` (this agent must NOT touch the barrel).
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Document types (project_artifacts.type) handled by the stage-4 editor
// ---------------------------------------------------------------------------
/**
 * The artifact types the editor / save endpoint supports (T02 acceptance).
 * Free-form storage (varchar(100)) — but the editor and the save route accept
 * exactly this whitelist so an arbitrary `type` can never be UPSERTed.
 */
export const EDITABLE_DOC_TYPES = [
    'jd_doc',
    'interview_questions',
    'inmails',
    'sourcing_sheet',
    'kickoff_sheet',
    'client_followup_email',
    'internal_notification',
];
/**
 * Doc types eligible for the generic `regenerate_document` flow. The four
 * "core" package documents are the only ones with a `regen_doc_<type>`
 * instruction row (T07); KS (`kickoff_sheet`) and FU (`client_followup_email`)
 * regenerate through their OWN dedicated ai actions (`generate_kickoff_sheet`,
 * `generate_followup_email`) — see Regen.md / sql-queries.md.
 */
export const REGENERATABLE_DOC_TYPES = [
    'jd_doc',
    'interview_questions',
    'inmails',
    'sourcing_sheet',
];
/** The four package documents that must all be `approved` before confirm. */
export const PACKAGE_DOC_TYPES = [
    'jd_doc',
    'interview_questions',
    'inmails',
    'sourcing_sheet',
];
/**
 * Artifact statuses that SATISFY the confirm gate for a package document.
 *
 * `sent` is here on purpose. The send path (`sendStage5Email` → `STAGE5_ARTIFACT_TYPE`)
 * OVERWRITES `project_artifacts.status` with `'sent'`, and `sourcing_sheet` — a member
 * of {@link PACKAGE_DOC_TYPES} — is sendable (`SENDABLE_DOC_TYPES`, E05-F06-T09), as
 * `inmails` was before PK-QA-27. So "Approve, then Send" left the row at `'sent'`, the
 * UI (which has always read `approved || sent`) enabled "Confirm & Move to Final", and
 * the server counted only `status='approved'` and answered "not all documents approved
 * (3 of 4)" — a package that can never be confirmed (rejected /aiac-verify 2026-07-10).
 *
 * A doc that was emailed to its recipient is by definition at least as far along as an
 * approved one, so widening the gate (rather than narrowing the UI) is the correct
 * direction. THIS list plus {@link packageGateState} is the ONE definition of the gate:
 * the api count (`services/docEditRegen.ts::confirmPackage`) and the button's disabled
 * state (`web/src/document/GenerationStageView.tsx`) must both read it, never re-spell
 * the predicate locally — that duplication IS the bug this replaces.
 */
export const PACKAGE_APPROVED_STATUSES = ['approved', 'sent'];
/** True when this artifact status counts as approved for the confirm gate. */
export function isPackageApprovedStatus(status) {
    return PACKAGE_APPROVED_STATUSES.includes(status ?? '');
}
/**
 * Evaluate the confirm gate from a status lookup. Pure, transport-free and shared, so
 * the button and the endpoint cannot disagree about what "all approved" means.
 *
 * @param statusOf resolves a package doc type to its stored artifact status
 *                 (`undefined`/`null`/`'not_generated'` → not approved).
 */
export function packageGateState(statusOf) {
    const missing = PACKAGE_DOC_TYPES.filter((t) => !isPackageApprovedStatus(statusOf(t)));
    const requiredCount = PACKAGE_DOC_TYPES.length;
    return {
        approvedCount: requiredCount - missing.length,
        requiredCount,
        allApproved: missing.length === 0,
        missing: [...missing],
    };
}
export const editableDocTypeSchema = z.enum(EDITABLE_DOC_TYPES);
export const regeneratableDocTypeSchema = z.enum(REGENERATABLE_DOC_TYPES);
/** Confidential doc types: the client name MUST be hidden in the body (Regen.md). */
export const CONFIDENTIAL_DOC_TYPES = ['jd_doc', 'inmails', 'interview_questions'];
export const CONFIDENTIAL_DISPLAY = '[CONFIDENTIAL - do not mention in document text]';
// ---------------------------------------------------------------------------
// T02 — save_stage4_artifact (PUT /projects/:id/artifacts/:type)
// ---------------------------------------------------------------------------
/**
 * The edited artifact content — a free-form jsonb object (the per-type document
 * shape; the editor builds it from the per-type form fields). The legacy save
 * persisted the whole `content` object verbatim with `status='edited'`,
 * `source='user'`.
 */
export const saveArtifactRequestSchema = z.object({
    /** The full edited document content (per-type jsonb object). */
    content: z.record(z.string(), z.unknown()),
});
/** Artifact row returned by save/approve/regenerate (project_artifacts row). */
export const artifactDtoSchema = z.object({
    id: z.number().int(),
    project_id: z.number().int(),
    type: z.string().min(1).max(100),
    status: z.string().min(1).max(50),
    source: z.string().min(1).max(50),
    content: z.record(z.string(), z.unknown()),
    /** ISO 8601 timestamp. */
    updated_at: z.string(),
});
export const saveArtifactResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    artifact: artifactDtoSchema,
});
// ---------------------------------------------------------------------------
// T03 — approve_artifact (POST /projects/:id/artifacts/:type/approve)
// ---------------------------------------------------------------------------
export const approveArtifactResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    artifact: artifactDtoSchema,
});
// ---------------------------------------------------------------------------
// T01 — regenerate_document (POST /projects/:id/artifacts/:type/regenerate)
// ---------------------------------------------------------------------------
/**
 * Regenerate one document with a custom instruction (Regen.md). The body carries
 * only the free-form instruction + general notes; `doc_type` is the URL param and
 * `userId` comes from the session (IDOR rule).
 */
export const regenerateDocumentRequestSchema = z.object({
    /** User instruction; empty → "None — generate standard version". */
    instruction: z.string().optional().default(''),
    /** General notes; pre-filled from projects.data.generation_notes by the UI. */
    generation_notes: z.string().optional().default(''),
});
/**
 * Regenerate response. The legacy node returned only `{success, doc_type,
 * projectId}` (the SPA re-fetched the artifact); v2 returns the freshly-saved
 * artifact directly so the UI can re-render without a round-trip — `doc_type` and
 * `projectId` are kept for parity.
 */
export const regenerateDocumentResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    doc_type: z.string().min(1).max(100),
    projectId: z.number().int(),
    artifact: artifactDtoSchema,
});
// ---------------------------------------------------------------------------
// T05 — confirm_package (POST /projects/:id/confirm-package)
// ---------------------------------------------------------------------------
/**
 * Confirm-package result (Confirm.md). The legacy node returned a single `result`
 * string ('ok' | 'error: not all documents approved (N of 4)'); v2 returns the
 * structured equivalent so the UI branches on a value, not a string. On success
 * the project is moved to the Final stage (legacy stage=4) and a `package_confirmed`
 * event is written; the .docx package is generated in the background (E08-F03).
 */
export const confirmPackageResponseSchema = z.object({
    success: z.boolean(),
    /** Count of approved package artifacts (of {@link PACKAGE_DOC_TYPES}.length). */
    approved_count: z.number().int(),
    /** Total required package artifacts (always {@link PACKAGE_DOC_TYPES}.length). */
    required_count: z.number().int(),
    /** Present on the failure path: the human reason ("not all documents approved …"). */
    error: z.string().optional(),
});
// ---------------------------------------------------------------------------
// PK-QA-36 — "Send Final Package" R2 link generation
// (POST /projects/:id/package/links)
// ---------------------------------------------------------------------------
/**
 * One clickable link for an EXISTING project artifact rendered to a Word `.doc`
 * and uploaded to R2 (PK-QA-36). Produced by the Launched-stage "Send Final
 * Package" endpoint — ONE entry per artifact that exists among the 6 generation
 * documents. Missing / not-generated docs are simply ABSENT (never an error), so
 * the array may hold fewer than 6 entries; the caller composes the package email
 * body from these links. `url` is the R2 `webViewLink` — PERMANENT when
 * `R2_PUBLIC_BASE_URL` is configured, otherwise a 7-day presigned GET URL
 * (services/r2Storage.ts). Rendering goes through the shared docRender serializer
 * so it is byte-identical to the web `.doc` export (no second grammar).
 */
export const packageLinkSchema = z.object({
    /** The artifact type (jd_doc / interview_questions / … — free string). */
    docType: z.string().min(1).max(100),
    /** Human label for the doc (exportLabel(docType), e.g. "Job Description"). */
    label: z.string(),
    /** The uploaded `.doc`'s clickable R2 URL. */
    url: z.string().url(),
});
/**
 * Response of POST /projects/:id/package/links: a BARE ARRAY of the generated
 * links, one per existing artifact (a project with no docs yet → `[]`).
 */
export const packageLinksResponseSchema = z.array(packageLinkSchema);
//# sourceMappingURL=doc-edit-regen.js.map